import { LegacyDashboardTypes, RegisterPage } from '@hologram-hyper-dashboard/components';
import analyticsEventBuilder from 'common-js/analytics';
import { getCurrentOrgId } from 'common-js/api/util';
import getStore from 'common-js/configureStore';
import { SIM_INVENTORY_ACTIVE, WELCOME, withContext } from 'common-js/constants/paths';
import useAppDispatch from 'common-js/hooks/useAppDispatch';
import { configureAccount, registerAccount } from 'common-js/reducers/account/actions';
import sendGaEvent from 'common-js/utils/marketing';
import { useCallback, useEffect, type FC } from 'react';
import { browserHistory, type RouteComponentProps } from 'react-router';
import { type RegisterAccountResponse } from '../../types/redux';

interface RegisterProps extends RouteComponentProps<{}, RouterQueryParams> {}

const Register: FC<RegisterProps> = ({ location }) => {
  const { inviteid, token, orgid, email } = location.query;
  const querySource = location.query.utm_source || 'dashboardDirectSignup';

  const dispatch = useAppDispatch();

  useEffect(() => {
    analyticsEventBuilder.pageView().page('Register').send();
  }, []);

  const onSubmit = useCallback(
    async (
      data: LegacyDashboardTypes.RegisterAccountParams
    ): Promise<LegacyDashboardTypes.RegisterResponse> => {
      try {
        const response: RegisterAccountResponse = await dispatch(registerAccount(data));

        // If user was registered successfully...
        if (typeof response === 'object' && response.user) {
          await dispatch(
            configureAccount(
              'my company', // TODO could remove this since company is always the default
              data.terms,
              data.orgInviteId ?? '',
              orgid ? parseInt(orgid, 10) : undefined,
              'company', // TODO could remove this since company is always the default
              querySource
            )
          );

          sendGaEvent('leadGenerated');

          // Get the current org context AFTER configureAccount completes
          const currentOrgId = getCurrentOrgId(getStore().getState());
          const redirectTo = withContext(
            `${inviteid ? SIM_INVENTORY_ACTIVE : WELCOME}${window.location.search}`,
            currentOrgId
          );

          // Redirect user after a brief loading delay
          window.setTimeout(() => {
            browserHistory.push(redirectTo);
          }, 3750);

          // Return success response to child component
          return {
            success: true,
            userId: response.user.userid,
          };
        }

        // Response is an error, throw it
        throw new Error(typeof response === 'string' ? response : 'Failed to register user');
      } catch (e) {
        throw new Error(e instanceof Error ? e.message : 'Registration failed. Please try again.');
      }
    },
    [dispatch, orgid, querySource, inviteid]
  );

  return (
    <RegisterPage
      loginUrl="/account/login"
      orgInviteId={inviteid}
      orgInviteToken={token}
      email={email}
      onSubmit={onSubmit}
    />
  );
};

export default Register;
